import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/common/PageHeader';
import { useToast } from '@/components/common/toast';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { Select2 } from '@/components/forms/Select2';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { autoMapApi } from '@/api/services/autoMap';
import { EMPTY_GUID, type AutoMapDto, type UnderlyingDto } from '@/api/types/autoMap';

const UPDATE_MSG = 'Are you sure you want to proceed with updating selected row?';

// Symbols can be a long pipe-separated list; show the first few + a 3-dots marker
// with a tooltip (title) listing all, so the column doesn't blow up the row width.
function SymbolsCell({ symbols }: { symbols: string }) {
  if (!symbols) return <span>-</span>;
  const parts = symbols.split('|').map((s) => s.trim()).filter(Boolean);
  const MAX = 3;
  if (parts.length <= MAX) return <span>{parts.join(' | ')}</span>;
  return (
    <span title={parts.join(' | ')} style={{ cursor: 'help' }}>
      {parts.slice(0, MAX).join(' | ')} <span className="text-primary fw-bold">…</span>
    </span>
  );
}

export function AutoMapPage() {
  const toast = useToast();
  const query = useQuery({
    queryKey: ['auto-map', 'mappings'],
    queryFn: () => autoMapApi.getSymbolMappings({ skipCount: 0, maxResultCount: 10000 }),
  });

  const [rows, setRows] = useState<AutoMapDto[]>([]);
  const [dropdowns, setDropdowns] = useState<Record<string, UnderlyingDto[]>>({});
  const [loadingDd, setLoadingDd] = useState<Record<string, boolean>>({});
  const [editingId, setEditingId] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{ message: string; onConfirm: () => void } | null>(null);
  const [busy, setBusy] = useState(false);

  // Local editable copy, resynced whenever the server data changes.
  useEffect(() => {
    if (query.data) setRows(query.data.items.map((r) => ({ ...r })));
  }, [query.data]);

  const patchRow = (id: string, patch: Partial<AutoMapDto>) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const loadDropdown = async (id: string) => {
    if (dropdowns[id] || loadingDd[id]) return;
    setLoadingDd((m) => ({ ...m, [id]: true }));
    try {
      const data = await autoMapApi.getDropdownData(id);
      setDropdowns((m) => ({ ...m, [id]: data }));
    } catch {
      setDropdowns((m) => ({ ...m, [id]: [] }));
    } finally {
      setLoadingDd((m) => ({ ...m, [id]: false }));
    }
  };

  const startEdit = (row: AutoMapDto) => {
    setEditingId(row.id);
    void loadDropdown(row.id);
  };
  // Legacy keeps a dropdown change in the DataTables row data regardless of the
  // per-row edit flow (index.js: the `select.form-select` change handler writes
  // rowData.underlyingId directly and is never reverted), so the value persists
  // outside edit-mode and is picked up by the bulk Save. Cancel only exits edit
  // mode; it does not revert the chosen underlying.
  const cancelEdit = () => setEditingId(null);

  const allChecked = rows.length > 0 && rows.every((r) => r.mapped);
  const toggleAll = (checked: boolean) => setRows((rs) => rs.map((r) => ({ ...r, mapped: checked })));

  const saveRow = (row: AutoMapDto) =>
    setConfirm({
      message: UPDATE_MSG,
      onConfirm: async () => {
        setBusy(true);
        try {
          await autoMapApi.update({ id: row.id, underlyingId: row.underlyingId || EMPTY_GUID, mapped: row.mapped, symbols: row.symbols });
          toast.success('Mapping updated.');
          setEditingId(null);
          setConfirm(null);
          await query.refetch();
        } catch {
          toast.error('Failed to update mapping.');
        } finally {
          setBusy(false);
        }
      },
    });

  const saveAll = () =>
    setConfirm({
      message: UPDATE_MSG,
      onConfirm: async () => {
        setBusy(true);
        try {
          await autoMapApi.bulkUpdate(rows.map((r) => ({ id: r.id, underlyingId: r.underlyingId || EMPTY_GUID, mapped: r.mapped, symbols: r.symbols })));
          toast.success('Mappings saved.');
          setConfirm(null);
          await query.refetch();
        } catch {
          toast.error('Failed to save mappings.');
        } finally {
          setBusy(false);
        }
      },
    });

  const columns: DataGridColumn<AutoMapDto>[] = [
    {
      key: 'mapped',
      title: (
        <input type="checkbox" className="form-check-input" checked={allChecked} onChange={(e) => toggleAll(e.target.checked)} title="Select all" />
      ),
      className: 'text-center',
      sortable: false,
      filterable: false,
      hideable: false,
      value: (r) => (r.mapped ? 'Yes' : 'No'),
      cell: (r) => (
        <input type="checkbox" className="form-check-input fk-mapped-checkbox" checked={r.mapped} onChange={(e) => patchRow(r.id, { mapped: e.target.checked })} />
      ),
    },
    {
      key: 'underlying',
      title: 'Underlying',
      className: 'text-start',
      value: (r) => r.underlying,
      cell: (r) =>
        editingId === r.id ? (
          <Select2
           
            placeholder={loadingDd[r.id] ? 'Loading…' : '-'}
            allowClear
            value={r.underlyingId ?? ''}
            options={(dropdowns[r.id] ?? []).map((o) => ({ value: o.id, label: o.name }))}
            onChange={(v) => patchRow(r.id, { underlyingId: v || null })}
          />
        ) : (
          r.underlying || '-'
        ),
    },
    {
      key: 'symbols',
      title: 'Symbols',
      className: 'text-start',
      value: (r) => r.symbols,
      cell: (r) => <SymbolsCell symbols={r.symbols} />,
    },
  ];

  return (
    <div className="auto-map-page">
      <PageHeader title="Auto Map" breadcrumb={['Home', 'Auto Map']} />

      <DataGrid
        columns={columns}
        data={rows}
        rowKey={(r) => r.id}
        columnSearch
        card
        includeExcel
        excelFileName="AutoMap"
        emptyMessage={query.isLoading ? 'Loading…' : 'No data.'}
        toolbarStart={
          <button type="button" className="btn btn-primary btn-sm" onClick={saveAll} disabled={busy || !rows.length}>
            <i className="fa fa-save me-1" />Save
          </button>
        }
        rowActions={(r) =>
          editingId === r.id ? (
            <span className="d-inline-flex gap-1">
              <button type="button" className="btn btn-outline-primary btn-sm" title="Save" onClick={() => saveRow(r)} disabled={busy}><i className="fa fa-check" /></button>
              <button type="button" className="btn btn-outline-danger btn-sm" title="Cancel" onClick={cancelEdit} disabled={busy}><i className="fa fa-times" /></button>
            </span>
          ) : (
            <button type="button" className="btn btn-outline-primary btn-sm" title="Edit" onClick={() => startEdit(r)}><i className="fa fa-edit" /></button>
          )
        }
      />

      <ConfirmDialog
        show={!!confirm}
        title="Are You Sure?"
        message={confirm?.message ?? ''}
        confirmLabel="Confirm"
        confirmVariant="primary"
        busy={busy}
        onConfirm={() => confirm?.onConfirm()}
        onCancel={() => { if (!busy) setConfirm(null); }}
      />
    </div>
  );
}
